/**
 * ShopifyFormSkinner
 *
 * Applies a custom visual skin to one or more Shopify Forms app embed
 * instances on the page. Shopify Forms renders via a <shopify-forms-embed>
 * web component using declarative open shadow DOM, so styling requires
 * either (a) overriding inherited CSS custom properties on :host, or
 * (b) injecting a stylesheet into the shadow root. This class does both
 * by injecting a single <style> element whose :host block sets tokens
 * and whose rules style the form's internals.
 *
 * Usage:
 *
 *   ShopifyFormSkinner.skin({
 *     id: 'unique-skin-id',
 *     selector: 'shopify-forms-embed#some-form-id',
 *     shadowCss: `
 *       :host { --my-color: #163a52; ... }
 *       ...selectors...
 *     `,
 *   });
 *
 * Teardown (e.g. from devtools console):
 *
 *   ShopifyFormSkinner.stop('unique-skin-id');
 *
 * Each skin instance:
 *  - waits for DOMContentLoaded if needed
 *  - scans the document for matching hosts and injects its style element
 *  - watches document.body via MutationObserver for hosts added later
 *    (lazy-loaded sections, modals, cart drawers)
 *  - dedupes via WeakSet so already-styled hosts are never re-processed
 *  - cleans up its observer and injected styles on stop()
 *
 * Architecture and theme integration: see
 * docs/shopify-forms-theme-integration.md
 */
class ShopifyFormSkinner {
  static #instances = new Map();

  /**
   * Create and start a skinner instance.
   * @param {{ id: string, selector?: string, shadowCss?: string }} config
   * @returns {ShopifyFormSkinner}
   */
  static skin(config) {
    const instance = new ShopifyFormSkinner(config);
    instance.start();
    ShopifyFormSkinner.#instances.set(instance.id, instance);
    return instance;
  }

  /**
   * Stop and tear down a skinner instance by id.
   * Removes the injected <style> from any styled hosts and disconnects
   * the MutationObserver.
   * @param {string} id
   */
  static stop(id) {
    const instance = ShopifyFormSkinner.#instances.get(id);
    instance?.destroy();
    ShopifyFormSkinner.#instances.delete(id);
  }

  /**
   * List currently registered skin ids. Useful for debugging.
   * @returns {string[]}
   */
  static list() {
    return [...ShopifyFormSkinner.#instances.keys()];
  }

  #observer = null;
  #styled = new WeakSet();

  constructor({ id, selector = 'shopify-forms-embed', shadowCss = '' } = {}) {
    if (!id) throw new Error('ShopifyFormSkinner: id is required');
    this.id = id;
    this.selector = selector;
    this.shadowCss = shadowCss;
    this.styleId = `tpm-form-skin-${id}`;
  }

  #apply = (host) => {
    if (this.#styled.has(host)) return;
    const root = host.shadowRoot;
    if (!root) return; // not yet hydrated; observer will retry on next mutation
    if (!root.getElementById(this.styleId)) {
      const style = document.createElement('style');
      style.id = this.styleId;
      style.textContent = this.shadowCss;
      root.appendChild(style);
    }
    this.#styled.add(host);
  };

  #scan = () => {
    document.querySelectorAll(this.selector).forEach(this.#apply);
  };

  #onMutate = (mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        if (node.matches?.(this.selector)) this.#apply(node);
        node.querySelectorAll?.(this.selector).forEach(this.#apply);
      }
    }
  };

  start() {
    const begin = () => {
      this.#scan();
      this.#observer = new MutationObserver(this.#onMutate);
      this.#observer.observe(document.body, { childList: true, subtree: true });
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', begin, { once: true });
    } else {
      begin();
    }
  }

  destroy() {
    this.#observer?.disconnect();
    this.#observer = null;
    document.querySelectorAll(this.selector).forEach((host) => {
      host.shadowRoot?.getElementById(this.styleId)?.remove();
    });
    this.#styled = new WeakSet();
  }
}

// Expose globally so inline section scripts can reference it.
if (typeof window !== 'undefined') {
  window.ShopifyFormSkinner = ShopifyFormSkinner;
}
